import { Service, inject } from '@angular/core'
import {
  CreateCustomerBody,
  CreateCustomerVisitBody,
  CustomerSchedule,
  CustomerVisitData,
  DeleteEntityResponse,
  GetCustomerScheduleQuery,
  GetCustomerVisitsResponse,
  GetCustomersResponse,
  GetHistoryResponse,
  ManagedCustomer,
  SetCustomerBlockedBody,
  SetCustomerCardIdBody,
  SetCustomerStatusBody,
  SetCustomerTrialHoursBody,
  UpdateCustomerBody,
  UpdateCustomerVisitBody,
} from '@seiri/shared/common'
import { ApiService } from '../api.service'

@Service()
export class ManageCustomerApiService {
  private readonly api = inject(ApiService)

  getCustomers(entityId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<GetCustomersResponse>(`manage/entities/${entityId}/customers`, { ...init })
  }

  getCustomer(entityId: string, customerId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(`manage/entities/${entityId}/customers/${customerId}`, {
      ...init,
    })
  }

  getCustomerSchedule(entityId: string, customerId: string, query: GetCustomerScheduleQuery = {}, init?: RequestInit) {
    const url = this.api.createURL(`manage/entities/${entityId}/customers/${customerId}/schedule`)
    url.search = this.api.toQueryParams(query)
    return this.api.protectedFetchJson<CustomerSchedule>(url, { ...init })
  }

  getCustomerHistory(entityId: string, customerId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<GetHistoryResponse>(
      `manage/entities/${entityId}/customers/${customerId}/history`,
      { ...init },
    )
  }

  createCustomer(entityId: string, body: CreateCustomerBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(`manage/entities/${entityId}/customers`, {
      method: 'POST',
      body: JSON.stringify(body),
      ...init,
    })
  }

  deleteCustomer(entityId: string, customerId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<DeleteEntityResponse>(`manage/entities/${entityId}/customers/${customerId}`, {
      method: 'DELETE',
      ...init,
    })
  }

  updateCustomer(entityId: string, customerId: string, body: UpdateCustomerBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(`manage/entities/${entityId}/customers/${customerId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      ...init,
    })
  }

  setCustomerStatus(entityId: string, customerId: string, body: SetCustomerStatusBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(`manage/entities/${entityId}/customers/${customerId}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...init,
    })
  }

  setCustomerBlocked(entityId: string, customerId: string, body: SetCustomerBlockedBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(`manage/entities/${entityId}/customers/${customerId}/block`, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...init,
    })
  }

  setCustomerTrialHours(entityId: string, customerId: string, body: SetCustomerTrialHoursBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(
      `manage/entities/${entityId}/customers/${customerId}/trial-hours`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        ...init,
      },
    )
  }

  setCustomerCard(entityId: string, customerId: string, body: SetCustomerCardIdBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ManagedCustomer>(`manage/entities/${entityId}/customers/${customerId}/card-id`, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...init,
    })
  }

  getCustomerVisits(entityId: string, customerId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<GetCustomerVisitsResponse>(
      `manage/entities/${entityId}/customers/${customerId}/visits`,
      { ...init },
    )
  }

  createCustomerVisit(entityId: string, customerId: string, body: CreateCustomerVisitBody, init?: RequestInit) {
    return this.api.protectedFetchJson<CustomerVisitData>(
      `manage/entities/${entityId}/customers/${customerId}/visits`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        ...init,
      },
    )
  }

  updateCustomerVisit(
    entityId: string,
    customerId: string,
    visitId: string,
    body: UpdateCustomerVisitBody,
    init?: RequestInit,
  ) {
    return this.api.protectedFetchJson<CustomerVisitData>(
      `manage/entities/${entityId}/customers/${customerId}/visits/${visitId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
        ...init,
      },
    )
  }

  deleteCustomerVisit(entityId: string, customerId: string, visitId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<DeleteEntityResponse>(
      `manage/entities/${entityId}/customers/${customerId}/visits/${visitId}`,
      {
        method: 'DELETE',
        ...init,
      },
    )
  }
}
